import React, { useContext, useMemo } from 'react';
import { OpenRegular } from '@fluentui/react-icons';
import {
  Button,
  Link as FluentLink,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Tooltip,
} from '@fluentui/react-components';

import DocumentContext from '@modules/documents/contexts/DocumentContext.context';
import { useIsSafari } from '@shared/hooks';
import useEditorActive from '../../../hooks/useEditorActive.hook';
import { TBlockDataVersion } from '../../../types/blocks.types';
import { getBlockDisplayName } from '../../../helpers/blocks.helper';
import { isRegistrationSettled } from '../../../helpers/isRegistrationSettled.helper';
import { TERMINOLOGY } from '@shared/i18n';
import NodeBubbleMenuShell from '../../../components/NodeBubbleMenuShell';
import { ETiptapSelectorKey } from '../../../types/tiptap.types';
import { LibraryIcon } from '@shared/icons';
import { Link } from 'react-router-dom';

const useStyles = makeStyles({
  link: {
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase300,
    fontSize: tokens.fontSizeBase300,
    maxWidth: '210px',
    whiteSpace: 'normal', // allow wrapping
    wordBreak: 'break-word', // break long words/URLs if needed
    marginRight: tokens.spacingHorizontalS,
  },
  noBorder: {
    ...shorthands.borderColor('transparent'),
  },
});

const InlineBlockNodeBubbleMenu = () => {
  const { doc, version, versionId, resolvedItemNames, editor } = useContext(DocumentContext);
  const classes = useStyles();
  const isSafari = useIsSafari(); // tooltips in a bubbleMenu do not work in Safari

  const attrs = useEditorActive((e) => e.getAttributes('inline-block'));

  const blockData: { url: string; name: string; active: boolean; externalUrl?: string; isQm?: boolean } | null =
    useMemo(() => {
      if (!attrs?.id) return null; // attrs is an empty object if it is not an inline-block
      const blockData = doc.blocksData[attrs.id];
      if (!blockData) return null;

      let blockVersionEntry: TBlockDataVersion | undefined = undefined;
      if (isRegistrationSettled(attrs) && blockData && versionId) blockVersionEntry = blockData.versions[versionId];

      let url = `/inline-block/${attrs.item}`;
      const params: string[] = [];
      if (blockVersionEntry) params.push(`view=${blockVersionEntry.blockVersion}`);
      if (blockVersionEntry?.variantFolderId) params.push(`variant=${blockVersionEntry.variantFolderId}`);
      if (params.length) url += `?${params.join('&')}`;

      const name = getBlockDisplayName(
        doc,
        version,
        resolvedItemNames,
        blockData,
        attrs.versionMode,
        blockVersionEntry
      );
      const externalUrl = blockVersionEntry?.url;
      return { url, name, active: blockData.itemActive, externalUrl, isQm: blockData.isQm };
    }, [doc.blocksData, attrs, version, versionId, resolvedItemNames]);

  const hasExternalUrl = !!blockData?.externalUrl;
  const externalHref = blockData?.externalUrl;

  // When an external URL is present, the name link opens the external URL;
  // otherwise it opens the inline-block page (default behavior).
  const header = !blockData ? (
    <span className={mergeClasses(classes.link, 'truncate', 'unstyled', 'text-red-500')}>
      Loading {TERMINOLOGY.inlineBlock.lower} failed!
    </span>
  ) : !blockData.active ? ( // keep it short, so it fits in the bubble menu:
    <span className={mergeClasses(classes.link, 'truncate', 'unstyled', 'text-red-500')}>
      {TERMINOLOGY.inlineBlock.upper} was archived!
    </span>
  ) : (
    <FluentLink
      href={hasExternalUrl ? externalHref : blockData.url}
      target="_blank"
      rel="noopener noreferrer"
      className={mergeClasses(classes.link, hasExternalUrl ? '' : 'unstyled')}
    >
      {blockData.name}
    </FluentLink>
  );

  const buttons = useMemo(
    () =>
      hasExternalUrl
        ? [
            {
              content: (
                <Link to={blockData?.url} target="_blank" rel="noopener noreferrer">
                  <Button
                    icon={<OpenRegular />}
                    aria-label={`Open ${TERMINOLOGY.inlineBlock.lower}`}
                    className={classes.noBorder}
                    size="small"
                  />
                </Link>
              ),
              tooltip: `Open ${TERMINOLOGY.inlineBlock.lower}`,
            },
          ]
        : [],
    [hasExternalUrl, blockData?.url]
  );

  // Use LibraryLink composite icon when external URL exists, GlobalLibrary for QM, plain Library otherwise
  const variant = hasExternalUrl ? 'url' : blockData?.isQm ? 'qm' : 'default';
  const leadingIcon = (
    <LibraryIcon
      variant={variant}
      filled={false}
      fontSize="24px"
      className={blockData?.active ? 'text-brand-80' : 'text-red-500'}
    />
  );

  return (
    <NodeBubbleMenuShell
      nodeName={ETiptapSelectorKey.INLINE_BLOCK}
      editor={editor}
      isEdit={true /* show in view mode too */}
      leadingIcon={leadingIcon}
      header={header}
    >
      {buttons.map(({ content, tooltip }) =>
        isSafari ? (
          <React.Fragment key={tooltip}>{content}</React.Fragment>
        ) : (
          <Tooltip key={tooltip} content={tooltip} relationship="label" withArrow>
            {content}
          </Tooltip>
        )
      )}
    </NodeBubbleMenuShell>
  );
};

export default InlineBlockNodeBubbleMenu;
